import React, { FormEvent, useEffect, useState } from "react";
import {
  getPropertyKinds,
  submitFeaturedPropertyRequest,
} from "@services/krent.service";
import { PropertyKind } from "@interfaces/properties.interface";
import { PROPERTY_FEATURED_FORM_ID } from "./scroll-to-featured-form";
import "./styles.css";

const benefits = [
  "No commitment required — free initial consultation",
  "In-house media team — no outsourcing needed",
  "KYC-verified buyers and tenants only",
  "You own all media assets produced for your property",
];

const packageOptions = [
  { value: "standard", label: "Standard" },
  { value: "premium", label: "Premium" },
  { value: "unsure", label: "I'm not sure yet" },
];

const PropertyFeaturedFormSection = () => {
  const [fullName, setFullName] = useState("");
  const [location, setLocation] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [packageInterest, setPackageInterest] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [propertyKinds, setPropertyKinds] = useState<PropertyKind[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  useEffect(() => {
    const abortController = new AbortController();

    getPropertyKinds(abortController.signal)
      .then((res) => setPropertyKinds(res.data))
      .catch(() => {});

    return () => abortController.abort();
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus("idle");

    const selectedPackage = packageOptions.find(
      (pkg) => pkg.value === packageInterest,
    );
    const selectedPropertyKind = propertyKinds.find(
      (kind) => kind.id === propertyType,
    );

    submitFeaturedPropertyRequest({
      fullName,
      propertyLocation: location,
      propertyType: selectedPropertyKind?.title || propertyType,
      packageInterest: selectedPackage?.label || packageInterest,
      phone,
      email,
    })
      .then((res) => {
        if (res.success) {
          setStatus("success");
          setFullName("");
          setLocation("");
          setPropertyType("");
          setPackageInterest("");
          setPhone("");
          setEmail("");
        }
      })
      .catch(() => setStatus("error"))
      .finally(() => setLoading(false));
  };

  return (
    <section id={PROPERTY_FEATURED_FORM_ID} className="property-featured-form">
      <div className="property-featured-container property-featured-form-layout">
        <div className="property-featured-form-info">
          <p className="property-featured-form-label font-onest">GET STARTED</p>
          <h2 className="property-featured-form-title font-onest">
            Get Your Property Featured
          </h2>
          <p className="property-featured-form-description font-onest">
            Complete the form and a Krent Property Advisor will contact you
            within <strong>12 hours</strong> to discuss the best package for
            your property.
          </p>

          <ul className="property-featured-form-benefits font-onest">
            {benefits.map((benefit) => (
              <li key={benefit}>
                <i className="fa-solid fa-check text-primary" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>

          <div className="property-featured-form-highlight font-onest">
            <h5 className="font-onest">
              Ready to Turn Your Property Into a Revenue-Generating Asset?
            </h5>
            <p className="font-onest">
              Get featured today and let Krent help the right people discover
              your property.
            </p>
          </div>
        </div>

        <div className="property-featured-form-card font-onest">
          <form onSubmit={handleSubmit}>
            <div className="property-featured-form-field font-onest">
              <label htmlFor="featured-full-name">Full Name</label>
              <input
                id="featured-full-name"
                type="text"
                placeholder="e.g. Emeka Okafor"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>

            <div className="property-featured-form-field font-onest">
              <label htmlFor="featured-location">Property Location</label>
              <input
                id="featured-location"
                type="text"
                placeholder="e.g. Lekki Phase 1, Lagos"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                required
              />
            </div>

            <div className="property-featured-form-field font-onest">
              <label htmlFor="featured-property-type">Property Type</label>
              <select
                id="featured-property-type"
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value)}
                required
              >
                <option value="">Select property type</option>
                {propertyKinds.map((kind) => (
                  <option key={kind.id} value={kind.id}>
                    {kind.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="property-featured-form-field font-onest">
              <label htmlFor="featured-package">Package Interest</label>
              <select
                id="featured-package"
                value={packageInterest}
                onChange={(e) => setPackageInterest(e.target.value)}
                required
              >
                <option value="">Select a package</option>
                {packageOptions.map((pkg) => (
                  <option key={pkg.value} value={pkg.value}>
                    {pkg.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="property-featured-form-field font-onest   ">
              <label htmlFor="featured-phone">Phone Number</label>
              <input
                id="featured-phone"
                type="tel"
                placeholder="+234 911 800 8008"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>

            <div className="property-featured-form-field font-onest">
              <label htmlFor="featured-email">Email Address</label>
              <input
                id="featured-email"
                type="email"
                placeholder="emeka@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {status === "success" && (
              <p className="property-featured-form-message success font-onest">
                Your request has been submitted successfully. A Krent Advisor
                will contact you within 12 hours.
              </p>
            )}

            {status === "error" && (
              <p className="property-featured-form-message error font-onest">
                Sorry, your request could not be submitted. Please try again.
              </p>
            )}

            <button
              type="submit"
              className="property-featured-form-submit font-onest "
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit Property"}
              {!loading && <i className="fa fa-arrow-right text-white" />}
            </button>

            <p className="property-featured-form-footer">
              A Krent Advisor will contact you within 12 hours.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default PropertyFeaturedFormSection;
